import { Helmet } from 'react-helmet-async';
import { useMediaQuery, Drawer, Card, CardHeader, CardContent, Grid, Alert, FormHelperText, Stack, Button, Box, Typography } from "@mui/material"
import { useRecordContext } from "ra-core"
import { FC, useState } from "react"
import StringArrayTextField from "@/components/input/StringArrayTextField"
import Confirm from "@/components/widgets/Confirm"
import WithSave from "@/controller/WithSave"
import { ClientSettingDto } from "@/gen/api/securefile"



interface EditClientScanFoldersProps {
  open: boolean;
  isMac: boolean;
  onClose: () => void;
}

interface AutoDiscSettings {
  scanRootFolders: string[];
  submit: any;
}

const initialVal = (record: ClientSettingDto | undefined) => ({
  settings: {
    organizationId: record?.organizationId,
    clientId: record?.clientId,
    autoDiscoveryEnabled: record?.autoDiscoveryEnabled,
    scanRootFolders: record?.scanRootFolders ?? [],
  },
  tmpRootFolderWarning: null as string|null,
  openMergeConfirm: false as boolean,
  parentToMerge: null as string|null,
  subsToMerge: null as string[]|null,
  filteredFolders: null as string[]|null,
  warningTimer: null as number|null,
  touched: null as {[k in keyof AutoDiscSettings]+?: boolean}|null,
  errors: null as {[k in keyof AutoDiscSettings]+?: string}|null,
})

export const EditClientScanFolders: FC<EditClientScanFoldersProps> = ({
  open,
  isMac,
  onClose
}) => {
  const match = useMediaQuery('(max-width: 680px)')
  const width = match ? 'calc(100vw - 50px)' : 630
  const record = useRecordContext<ClientSettingDto>()
  const [states, setStates] = useState(initialVal(record))
  const validate = (s1: typeof states) => {
    const errors: any[] = []
    // if (s1.settings.autoDiscoveryEnabled && s1.settings.scanRootFolders.length === 0) {
    //   errors.push({ scanRootFolders: 'Scan Root Folders is required' })
    // }
    return errors.length === 0 ? null : errors.reduce((acc, x) => ({...acc, ...x}), {})
  }
  const handleDialogClose = () => {
    setStates(v => ({...v, openMergeConfirm: false, parentToMerge: null, subsToMerge: null}))
  }

  const handleMerge = () => {
    if(!states.subsToMerge) { return }
    setStates(v=>{
      let patch = null as any
      if (v.warningTimer) { window.clearTimeout(v.warningTimer) }
        patch = {
          tmpRootFolderWarning: 'Subfolders have been merged.',
          warningTimer: window.setTimeout(() => {
            setStates(v1 => ({...v1, tmpRootFolderWarning: null, warningTimer: null}))
          }, 5000)
        }
      return {...v,
          openMergeConfirm: false,
          settings: {...v.settings, scanRootFolders: v.filteredFolders},
          touched: {...v.touched, scanRootFolders: true},
          parentToMerge: null, 
          subsToMerge: null,
          filteredFolders: null,
          ...patch
        }
    })
  }
  const alertContent = isMac ? alertDescUnix : alertDesc
  const filterFolders = isMac ? filterFolderListWithoutSubUnix : filterFolderListWithoutSub
  const validateRootFolder = isMac ? validateRootFolderStringUnix : validateRootFolderString
  return (
    <Drawer anchor="right" PaperProps={{ sx: { p: 2, width, }}}
      onClose={onClose}
      open={open}
    >
      {record &&
        <>
        {open && 
          <Helmet>
            <title>{"Edit Scan Folders"} | OnData</title>
          </Helmet>}
          <Card>
            <CardHeader title={"Edit Scan Folders"} />
            <CardContent>
              <Grid container spacing={4} sx={{ minHeight: 100, maxHeight: 600, overflow: "auto" }}>
                <Grid item xs={12}>
                  <Alert severity="info" sx={{mt: -2 }}>
                    {alertContent}
                  </Alert>
                  <StringArrayTextField list={states.settings.scanRootFolders} stackChips="column"
                    sx={{ mt: 3 }}
                    label="Scan Root Folders"
                    placeholder="Add a new Root Folder and press the Enter key to confirm..."
                    error={Boolean(states.touched?.scanRootFolders && states.errors?.scanRootFolders)}
                    helperText={states.touched?.scanRootFolders && states.errors?.scanRootFolders}
                    setList={(ls) => {
                      const filtered = filterFolders(ls)
                      if(filtered !== ls){
                        const subs = ls.filter(item => !filtered.includes(item))
                        setStates(v => ({...v,
                          openMergeConfirm: true,
                          subsToMerge: subs,
                          filteredFolders: filtered
                        }))
                      } else {
                        setStates(v => {
                          const touched = ls !== v.settings.scanRootFolders
                          const patch = null as any
                          return !touched ? v : {...v,
                            settings: {...v.settings, scanRootFolders: filtered},
                            touched: {...v.touched, scanRootFolders: true},
                            ...patch
                          }
                        })
                      }
                    }}
                    acceptValue={(s) => {
                      const errorStr = validateRootFolder(states.settings.scanRootFolders, s)
                      if (errorStr != null) {
                        setTimeout(() => {  
                          setStates(v => {
                            if (v.warningTimer) { window.clearTimeout(v.warningTimer) }
                            return {...v,
                              tmpRootFolderWarning: errorStr,
                              warningTimer: window.setTimeout(() => {
                                setStates(v1 => ({...v1, tmpRootFolderWarning: null, warningTimer: null}))
                              }, 5000)
                            }
                          })
                        })
                      }
                      setStates(v=>({...v, parentToMerge: s}))
                      return !errorStr
                    }}
                  />
                  {states.tmpRootFolderWarning &&
                    <FormHelperText sx={{ color: 'warning.main', ml: 2 }}>
                      {states.tmpRootFolderWarning}
                    </FormHelperText>}
                </Grid>
              </Grid>
            </CardContent>
            <Stack direction="row" justifyContent="space-between" sx={{ p: 2 }} >
              <Button color="primary" type="button" variant="outlined"
                onClick={onClose}
              >
                Cancel
              </Button>
              <WithSave render={({ save, saving}) =>
                <Button color="primary" type="submit" variant="contained"
                  disabled={!states.touched || saving}
                  onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
                    setStates(v => {
                      const v1 = {...v,
                        touched: {
                          autoDiscoveryEnabled: true,
                          scanRootFolders: true,
                        },
                      }
                      v1.errors = validate(v1)
                      if (!v1.errors) {  
                        setTimeout(() => {
                          save({...v1.settings}, {
                            onSuccess: () => {
                              setStates(v2 => ({...v2, touched: null}))
                              // onClose()
                            },
                            onFailure: (err:any) => {
                              setStates(v2 => ({...v2, errors: { submit: err.message }}))
                            },
                          })
                        })
                      }
                      return v1
                    })
                  }}
                >
                  Save
                </Button>}
              />
            </Stack>
          </Card>
        </>
      }
      <Confirm
        isOpen={states.openMergeConfirm}
        title={"Confirm Merge Folders"}
        loading={false}
        onConfirm={handleMerge}
        onClose={handleDialogClose}
        confirm="Confirm"
        cancel="Cancel"
        content={(
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', wordBreak: 'break-word' }}>
              <Box>
                {!!states.parentToMerge && 
                  <Typography variant="body2" sx={{ ml: 1, wordBreak: 'break-all' }}>
                    You have added a parent folder {<b>{` "${states.parentToMerge}" `}</b>} that contains the following existing sub-folders. 
                  </Typography>
                }
                <Box 
                  sx={{ mt: 2, px: 3, py: 1,
                    borderRadius: '8px', 
                    display: 'flex',
                    flexDirection: 'column',
                    maxHeight: 150,
                    overflow: 'auto',
                    bgcolor: theme => theme.palette.background.default 
                  }}
                >
                  {states.subsToMerge?.map((t, i) => (
                    <Typography key={i} component="li" variant="caption">
                      <b>{t}</b>
                    </Typography>
                  ))}
                </Box>
                <Typography variant="body2" sx={{ ml: 1, mt: 2 }} >
                  Such sub-folders are going to be removed from this list because the parent folder will get them automatically included for the scan.
                </Typography>
              </Box>
            </Box>
          </Box>
        )}
      />
    </Drawer>
  )
}

const alertDesc     = <>Please enter a list of root folders that you would like to scan for sensitive data. All subfolders within the root folders will be scanned recursively. You may use environmental variables in the root folders, such as <b>%UserProfile%</b>\Documents.</>
const alertDescUnix = <>Please enter a list of root folders that you would like to scan for sensitive data. All subfolders within the root folders will be scanned recursively. You may use environmental variables in the root folders, such as <b>$HOME</b>/Documents.</>


export const {isValidPathFormat, validateRootFolderString, filterFolderListWithoutSub} = (() => { 
  const formatPattern = /^[a-zA-Z]{1}\:\\.+$/i
  const formatPattern2 = /^\%[a-zA-Z0-9_\s]+\%[\\\w\s\.]*.*$/i
  const formatPattern3 = /^\\\\[^\\\/:*?""<>|\r\n]+\\.+$/i
  const banList = [
    "C:",
    "C:\\",
    "C:\\Users"
  ]
  const patternList = [
    /^[a-zA-Z]{1}\:\\program files(\s\(x86\))?[\\\w\s\.]*$/i,
    /^[a-zA-Z]{1}\:\\\$[\w\.]+[\\\w\s\.]*$/i,
    /^\%userprofile\%[\\]*$/i,
    /^c\:\\users\\[\w\s\.]+\\*$/i,
    /^c\:\\programdata[\\\w\s\.]*$/i,
    /^c\:\\recovery[\\\w\s\.]*$/i,
    /^c\:\\windows[\\\w\s\.]*$/i,
    /^c\:\\onedrivetemp[\\\w\s\.]*$/i,
    /^c\:\\users\\[\w\s\.]+\\appdata[\\\w\s\.]*$/i,
    /^c\:\\users\\[\w\s\.]+\\ondata[\\\w\s\.]*$/i
  ]
  const normalize = (s:string) => {
    return s.trim().replace(new RegExp('\\\\$','g'), '').toLowerCase()
  }
  const ckeckDuplication = (currentStr: string, challengeStr: string): string|undefined => {
    if (currentStr === challengeStr) {
      return 'Same value already exists.'
    }
    //do not allow to add C:\My Folder\Sub Folder if C:\My Folder already exists
    if (challengeStr.length > currentStr.length 
     && challengeStr.indexOf(currentStr + '\\') === 0
    ) {
      return 'A parent folder of the input value already exists.'
    }
  }
  const isValidPathFormat = (challengeStr: string) => {
    return formatPattern.test(challengeStr.trim()) || formatPattern2.test(challengeStr.trim()) || formatPattern3.test(challengeStr.trim())
  }
  return {
    isValidPathFormat,
    validateRootFolderString: (currentList: string[], challengeStr: string) => {
      if(!isValidPathFormat(challengeStr)){
        return 'This does not appear to be a valid folder.'
      }
      const nChallengeStr = normalize(challengeStr)
      for(const current of currentList){
        const nCurrent = normalize(current)
        const errorStr = ckeckDuplication(nCurrent, nChallengeStr)
        if (errorStr != null) { return errorStr }
      }
      for(const ban of banList){
        if(ban.toLowerCase() === nChallengeStr){
          return 'This value is not allowed.'
        }
      }
      for(const pattern of patternList){
        if(pattern.test(nChallengeStr)){
          return 'This value is not allowed.'
        }
      }
    },
    filterFolderListWithoutSub: (currentList: string[]) => {
      const pairs = currentList.map((s, i) => [normalize(s), i] as const).sort((a, b) => a[0].length <= b[0].length ? -1 : 1)
      const subfolders = new Set<number>()
      for(let j = 0; j < pairs.length - 1; j++) {
        const [s, i] = pairs[j]
        if (subfolders.has(i)) { continue; }
        const s_ = s + '\\'
        for(let n = j + 1; n < pairs.length; n++) {
          const other = pairs[n]
          if (subfolders.has(other[1])) { continue; }
          if (other[0].indexOf(s_) === 0) {
            subfolders.add(other[1])
          }
        }
      }
      return subfolders.size === 0 ? currentList :
        currentList.filter((s, i) => !subfolders.has(i))
    }
  }
})();

export const {isValidPathFormatUnix, validateRootFolderStringUnix, filterFolderListWithoutSubUnix}  = (() => {
  const formatPattern = /^\/users\/[\w\s\.]+\/[^\.].*$/i
  const formatPattern2 = /^\$[a-zA-Z0-9_\s]+\/[^\.].*$/i
  const formatPattern3 = /^\~\/[^\.].*$/i
  const banList = [
    "/",
    "/Applications",
    "/System",
    "/Library"
  ]
  const patternList = [
    /^\/users\/[\w\s\.]+\/library[\/\w\s\.]*$/i,
    /^\$[a-zA-Z0-9_\s]+\/library[\/\w\s\.]*$/i,
    /^\~\/library[\/\w\s\.]*$/i,
    /^\/users\/[\w\s\.]+\/ondata[\/\w\s\.]*$/i,
    /^\$[a-zA-Z0-9_\s]+\/ondata[\/\w\s\.]*$/i,
    /^\~\/ondata[\/\w\s\.]*$/i,
  ]
  const normalize = (s:string) => {
    return s.trim().replace(new RegExp(/\/+$/g), '').toLowerCase()
  }
  const ckeckDuplication = (currentStr: string, challengeStr: string): string|undefined => {
    if (currentStr === challengeStr) {
      return 'Same value already exists.'
    }
    //do not allow to add C:\My Folder\Sub Folder if C:\My Folder already exists
    if (challengeStr.length > currentStr.length 
     && challengeStr.indexOf(currentStr + '/') === 0
    ) {
      return 'A parent folder of the input value already exists.'
    }
  }

  const isValidPathFormatUnix = (challengeStr: string) => {
    return formatPattern.test(challengeStr.trim()) || formatPattern2.test(challengeStr.trim()) || formatPattern3.test(challengeStr.trim())
  }
  return {
    isValidPathFormatUnix,
    validateRootFolderStringUnix: (currentList: string[], challengeStr: string) => {
      if(!isValidPathFormatUnix(challengeStr)){
        return 'This value is not allowed.'
      }
      const nChallengeStr = normalize(challengeStr)
      for(const current of currentList){
        const nCurrent = normalize(current)
        const errorStr = ckeckDuplication(nCurrent, nChallengeStr)
        if (errorStr != null) { return errorStr }
      }
      for(const ban of banList){
        if(ban.toLowerCase() === nChallengeStr){
          return 'This value is not allowed.'
        }
      }
      for(const pattern of patternList){
        if(pattern.test(nChallengeStr)){
          return 'This value is not allowed.'
        }
      }
    },
    filterFolderListWithoutSubUnix: (currentList: string[]) => {
      const pairs = currentList.map((s, i) => [normalize(s), i] as const).sort((a, b) => a[0].length <= b[0].length ? -1 : 1)
      const subfolders = new Set<number>()
      for(let j = 0; j < pairs.length - 1; j++) {
        const [s, i] = pairs[j]
        if (subfolders.has(i)) { continue; }
        const s_ = s + '/'
        for(let n = j + 1; n < pairs.length; n++) {
          const other = pairs[n]
          if (subfolders.has(other[1])) { continue; }
          if (other[0].indexOf(s_) === 0) {
            subfolders.add(other[1])
          }
        }
      }
      return subfolders.size === 0 ? currentList :
        currentList.filter((s, i) => !subfolders.has(i))
    }
  }
})();
